/**
 * a class that includes helper functions for validating values against a certain type
 */
export default class ParameterValidator {
	
	name;
	options;

	/**
	 * 
	 * @param {Object|String} instanceOrName a class instance (this) or string
	 */
	constructor( instanceOrName ) {

		this.setName( instanceOrName );

	}

	/**
	 * 
	 * @param {Object|String} instanceOrName a class instance (this) or string
	 */
	setName( instanceOrName ) {

		if ( instanceOrName == undefined ) {
			throw new TypeError( `ParameterValidator :: a class instance (usually \`this\`) or name must be passed` );
		}

		if ( typeof instanceOrName === 'string' ) {
			
			this.name = instanceOrName;

		}
		else {

			this.name = instanceOrName.constructor.name;

			if ( this.name === 'Function' ) {
				this.name = instanceOrName.name;
			}

		}

	}

	_test( { key, value, condition, messagePartial, throwError, returnValue, returnMessage } ) {

		if ( condition ) {

			const message = `${ this.name } :: \`${ key }\` must be ${ messagePartial }`;
			
			if ( throwError ) {
				throw new TypeError( message );
			}

			return ( returnMessage ) ? message : false;
		
		}

		return ( returnValue ) ? value : true;

	}

	/**
	 * 
	 * @param {String} key a string representing the variable name used within the TypeError message
	 * @param {any} value the variable to be evaluated
	 * @param {Object} [options] optional parameters
	 */
	isDefined( key, value, { throwError = true, returnValue = true, returnMessage = true } = {} ) {

		return this._test({
			condition: ( value == undefined ),
			messagePartial: `defined`,
			key,
			value,
			throwError,
			returnValue,
			returnMessage
		});

	}

	/**
	 * 
	 * @param {String} key a string representing the variable name used within the TypeError message
	 * @param {any} value the variable to be evaluated
	 * @param {any} value a constructor reference (class name) (e.g. Array, HTMLElement)
	 * @param {Object} options optional parameters
	 */
	isInstance( key, value, constructor, { required = true, throwError = true, returnValue = true, returnMessage = true } = {} ) {

		return this._test({
			condition: ( required && !(value instanceof constructor) ),
			messagePartial: `an instance of \`${ constructor.name }\``,
			key,
			value,
			throwError,
			returnValue,
			returnMessage
		});

	}

	/**
	 * 
	 * @param {String} key a string representing the variable name used within the TypeError message
	 * @param {Object} value the variable to be evaluated
	 * @param {Object} options optional parameters
	 */
	isObject( key, value, { required = true, throwError = true, returnValue = true, returnMessage = true } = {} ) {

		return this._test({
			condition: ( required && !(typeof value === 'object') ),
			messagePartial: `an object`,
			key,
			value,
			throwError,
			returnValue,
			returnMessage
		});

	}

	/**
	 * 
	 * @param {String} key a string representing the variable name used within the TypeError message
	 * @param {Array} value the variable to be evaluated
	 * @param {Object} options optional parameters
	 */
	isArray( key, value, { required = true, throwError = true, returnValue = true, returnMessage = true } = {} ) {

		return this._test({
			condition: ( required && !(Array.isArray( value )) ),
			messagePartial: `an array`,
			key,
			value,
			throwError,
			returnValue,
			returnMessage
		});

	}

	/**
	 * 
	 * @param {String} key a string representing the variable name used within the TypeError message
	 * @param {Function} value the variable to be evaluated
	 * @param {Object} options optional parameters
	 */
	isFunction( key, value, { required = true, throwError = true, returnValue = true, returnMessage = true } = {} ) {

		return this._test({
			condition: ( required && !(typeof value === 'function') ),
			messagePartial: `a function`,
			key,
			value,
			throwError,
			returnValue,
			returnMessage
		});

	}

	/**
	 * 
	 * @param {String} key a string representing the variable name used within the TypeError message
	 * @param {String} value the variable to be evaluated
	 * @param {Object} options optional parameters
	 */
	isString( key, value, { required = true, throwError = true, returnValue = true, returnMessage = true } = {} ) {

		return this._test({
			condition: ( required && !(typeof value === 'string') ),
			messagePartial: `a string`,
			key,
			value,
			throwError,
			returnValue,
			returnMessage
		});

	}

	/**
	 * 
	 * @param {String} key a string representing the variable name used within the TypeError message
	 * @param {Boolean} value the variable to be evaluated
	 * @param {Object} options optional parameters
	 */
	isBoolean( key, value, { required = true, throwError = true, returnValue = true, returnMessage = true } = {} ) {

		return this._test({
			condition: ( required && !(typeof value === 'boolean') ),
			messagePartial: `a boolean`,
			key,
			value,
			throwError,
			returnValue,
			returnMessage
		});

	}

	/**
	 * 
	 * @param {String} key a string representing the variable name used within the TypeError message
	 * @param {Number} value the variable to be evaluated
	 * @param {Object} options optional parameters
	 */
	isNumber( key, value, { required = true, throwError = true, returnValue = true, returnMessage = true } = {} ) {

		return this._test({
			condition: ( required && !(Number( value ) === value) ),
			messagePartial: `a number`,
			key,
			value,
			throwError,
			returnValue,
			returnMessage
		});

	}
	
	/**
	 * 
	 * @param {String} key a string representing the variable name used within the TypeError message
	 * @param {Int} value the variable to be evaluated
	 * @param {Object} options optional parameters
	 */
	isInt( key, value, { required = true, throwError = true, returnValue = true, returnMessage = true } = {} ) {

		return this._test({
			condition: ( required && !(Number( value ) === value && value % 1 === 0) ),
			messagePartial: `an integer`,
			key,
			value,
			throwError,
			returnValue,
			returnMessage
		});

	}

	/**
	 * 
	 * @param {String} key a string representing the variable name used within the TypeError message
	 * @param {Float} value the variable to be evaluated
	 * @param {Object} options optional parameters
	 */
	isFloat( key, value, { required = true, throwError = true, returnValue = true, returnMessage = true } = {} ) {

		return this._test({
			condition: ( required && !(Number( value ) === value && value % 1 !== 0) ),
			messagePartial: `a float`,
			key,
			value,
			throwError,
			returnValue,
			returnMessage
		});

	}

	/**
	 * 
	 * @param {String} key a string representing the variable name used within the TypeError message
	 * @param {HTMLElement|String} value the variable to be evaluated
	 * @param {Object} options optional parameters
	 */
	isElement( key, value, { required = true, throwError = true, returnValue = true, returnMessage = true, container = document } = {} ) {
		
		if ( typeof value === 'string' ) {
			value = container.querySelector( value );
		}

		return this._test({
			condition: ( required && !(value instanceof HTMLElement) ),
			messagePartial: `an element or valid selector`,
			key,
			value,
			throwError,
			returnValue,
			returnMessage
		});

	}

	/**
	 * 
	 * @param {String} key a string representing the variable name used within the TypeError message
	 * @param {HTMLCollection|NodeList|String} value the variable to be evaluated
	 * @param {Object} options optional parameters
	 */
	isElementList( key, value, { required = true, throwError = true, returnValue = true, returnMessage = true, container = document } = {} ) {

		if ( typeof value === 'string' ) {
			value = container.querySelectorAll( value );
		}

		return this._test({
			condition: ( required && !(value instanceof HTMLCollection) && !(value instanceof NodeList) ),
			messagePartial: `a list of elements or valid selector`,
			key,
			value,
			throwError,
			returnValue,
			returnMessage
		});

	}

	/**
	 * 
	 * @param {String} key a string representing the variable name used within the TypeError message
	 * @param {String} value the variable to be evaluated
	 * @param {Object} options optional parameters
	 */
	isUrl( key, value, { required = true, throwError = true, returnValue = true, returnMessage = true } = {} ) {

		return this._test({
			condition: ( required && !/^(https?):\/\/[^\s$.?#].[^\s]*$/gm.test( value ) ),
			messagePartial: `a valid url`,
			key,
			value,
			throwError,
			returnValue,
			returnMessage
		});

	}

};